// SEO + structured data. Written 2026-08-30 against FAR_SEO_Production_Strategy.md
// and FAR_NAP_and_Local_Listings.md.
//
// WHY THIS IS A RUNTIME INJECTION: this build has no index.html to edit, so the
// head tags and JSON-LD are applied on mount by components/SeoHead.tsx. Google
// does execute JS and will pick these up, but server-rendered head tags are
// strictly better. If the site ever moves off this build, move all of this into
// the real <head> and delete SeoHead.
//
// HOST: the bare domain is canonical. Verified 2026-08-30 by loading
// https://www.futureastronautco.com/ and landing on https://futureastronautco.com/,
// so www redirects to non-www. A canonical or og:url pointing at a URL that
// redirects splits the signal, so SITE_URL must stay on the bare host. If the
// redirect direction is ever flipped at the DNS/host level, flip this too.
//
// NAP IS CANONICAL. Name, phone and locality below must match
// FAR_NAP_and_Local_Listings.md character for character. NAP drift across the
// site, Google Business Profile and directories is a direct local-ranking
// penalty. Do not edit these in isolation.
//
// STREET ADDRESS IS DELIBERATELY OMITTED. Future Astronaut Co. is registered as
// a service-area business with the GBP address hidden; publishing 480 Apollo Ave
// in schema would contradict the listing. Locality, region, postcode, country only.
//
// GEAR MODEL NUMBERS ARE DELIBERATELY ABSENT. Listing real inventory (desks,
// line arrays, fixtures) is the single highest-value differentiator in this
// market, but nobody has documented FAR's actual inventory yet. Do not invent it.

export const SITE_URL = 'https://futureastronautco.com/';

export const SEO = {
  title: 'Gulf Coast Stage Production, Lighting & Sound | Future Astronaut Co.',
  description:
    'Stage production, lighting, sound and LED for festivals, venues and promoters across the Gulf Coast. Shows, a record label and full production since 2015.',
  ogImage:
    'https://res.cloudinary.com/kn1rv77n/image/upload/f_auto,q_auto,w_1200/far/space/hero-nebula',
};

// Canonical NAP. Source of truth: FAR_NAP_and_Local_Listings.md.
export const NAP = {
  name: 'Future Astronaut Co.',
  phoneDisplay: '(251) 375-6486',
  phoneE164: '+1-251-375-6486',
  locality: 'Gulf Shores',
  region: 'AL',
  postalCode: '36542',
  country: 'US',
};

// Service area in ranking-priority order. Proximity is a local ranking factor,
// so this order matters and is NOT the same as the Pensacola-first ordering in
// the older SEO doc. Orange Beach and Foley are the easy wins nobody contests.
const AREA_SERVED = [
  { name: 'Gulf Shores', region: 'AL' },
  { name: 'Orange Beach', region: 'AL' },
  { name: 'Foley', region: 'AL' },
  { name: 'Pensacola', region: 'FL' },
  { name: 'Mobile', region: 'AL' },
  { name: 'Destin', region: 'FL' },
];

const SERVICE_CATALOG = [
  { name: 'Stage & Rigging', description: 'Decks, roofs, truss and load plots for any footprint.' },
  { name: 'Lighting Design', description: 'Programmed looks, fixtures and timecode for every set.' },
  { name: 'Sound Systems', description: 'Line array design, tuning and monitor world.' },
  { name: 'LED & Visuals', description: 'Panels, processing and content playback.' },
  { name: 'Show Production', description: 'Advancing, scheduling and day-of show calling.' },
];

// KEEP IN SYNC WITH data/site.ts EVENTS. The display strings there ('Sep 6-7,
// 2026' / '8PM') cannot be parsed reliably, so the machine-readable dates live
// here. All venues are US Central time; Sep and Oct 2026 are still CDT (-05:00).
// When an event is added or removed from EVENTS, mirror it here or the markup
// goes stale, which is worse than having none.
const EVENT_SCHEMA = [
  {
    name: 'LDW ft. Flux Pavilion + Yookie',
    startDate: '2026-09-06T20:00:00-05:00',
    endDate: '2026-09-08T02:00:00-05:00',
    venue: "Laguna's Beach Bar + Grill",
    locality: 'Pensacola',
    region: 'FL',
    url: 'https://www.eventbrite.com/e/ldw-at-lagunas-featuring-flux-pavilion-tickets-1994237706664',
  },
  {
    name: 'Niiko x Swae - Rooftop',
    startDate: '2026-09-12T17:00:00-05:00',
    venue: 'Hotel Effie Sandestin',
    locality: 'Miramar Beach',
    region: 'FL',
    url: 'https://www.eventbrite.com/e/niiko-x-swae-miramar-beach-fl-tickets-1997979680007',
  },
  {
    name: 'Costa',
    startDate: '2026-10-09T20:00:00-05:00',
    endDate: '2026-10-11T02:00:00-05:00',
    venue: "Laguna's Beach Bar + Grill",
    locality: 'Pensacola',
    region: 'FL',
    url: 'https://www.eventbrite.com/e/costa-pensacola-beach-fl-tickets-1992070693068',
  },
];

export function buildJsonLd() {
  const business = {
    '@type': 'LocalBusiness',
    additionalType: 'https://schema.org/EntertainmentBusiness',
    '@id': `${SITE_URL}#business`,
    name: NAP.name,
    alternateName: 'Future Astronaut Production',
    url: SITE_URL,
    telephone: NAP.phoneE164,
    foundingDate: '2015',
    description:
      'Stage production, lighting, sound and LED for festivals, venues and promoters across the Gulf Coast: Gulf Shores, Orange Beach, Foley, Mobile and Pensacola. Running shows on this coast since 2015.',
    image: SEO.ogImage,
    address: {
      '@type': 'PostalAddress',
      addressLocality: NAP.locality,
      addressRegion: NAP.region,
      postalCode: NAP.postalCode,
      addressCountry: NAP.country,
    },
    areaServed: AREA_SERVED.map((a) => ({
      '@type': 'City',
      name: a.name,
      addressRegion: a.region,
    })),
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Event Production Services',
      itemListElement: SERVICE_CATALOG.map((s) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: s.name, description: s.description },
      })),
    },
    sameAs: [
      'https://instagram.com/futureastronautco',
      'https://instagram.com/futureastronautrecords',
      'https://tiktok.com/@futureastronautco',
      'https://soundcloud.com/futureastronautco',
    ],
  };

  const events = EVENT_SCHEMA.map((e) => ({
    '@type': 'Event',
    name: e.name,
    startDate: e.startDate,
    ...(e.endDate ? { endDate: e.endDate } : {}),
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    url: e.url,
    location: {
      '@type': 'Place',
      name: e.venue,
      address: {
        '@type': 'PostalAddress',
        addressLocality: e.locality,
        addressRegion: e.region,
        addressCountry: 'US',
      },
    },
    organizer: { '@type': 'Organization', name: NAP.name, url: SITE_URL },
  }));

  return { '@context': 'https://schema.org', '@graph': [business, ...events] };
}
